import { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { FaPlus, FaTrash, FaEdit, FaArrowLeft, FaTimesCircle } from "react-icons/fa";

export default function PurchaseBillEntryForm({ onClose, editData = null, onSuccess = null }) {
    const [vendors, setVendors] = useState([]);
    const [trims, setTrims] = useState([]);
    const [fabrics, setFabrics] = useState([]);
    const [sizeVariants, setSizeVariants] = useState([]); 
    const [itemType, setItemType] = useState("trim");
    const [isNewStock, setIsNewStock] = useState(false); 
    
    const [itemList, setItemList] = useState([]);
    const [editingIndex, setEditingIndex] = useState(null); 

    const [formData, setFormData] = useState({
        purchase_no: "",
        date: new Date().toISOString().split("T")[0],
        invoice_no: "",
        invoice_date: "",
        vendor_id: "",
        vendor_name: "",
        phone: "",
        address: "",
        raw_material_id: "",
        fabric_id: "",
        size_id: "", 
        unit: "",
        quantity: "",
        rate: "",
        amount: "",
        item_name: "", 
        finish: "",
        length: "",
        width: "",
        gsm: "",
        shade_no: "",
        roll_no: "",
        colour: "",
        size: "",
        size_type: "",
        status: "active"
    });

    // Function to fetch and generate the next Purchase Number
    const fetchLatestPurchaseNo = async () => {
        try {
            const response = await axios.get("/purchase_bill/latest_no");
            // Assuming backend returns { last_no: "PUR-001" } or similar
            // If no records exist, we start with a default like PUR-001
            const lastNo = response.data.last_no;
            if (lastNo) {
                const prefix = lastNo.match(/[a-zA-Z-]+/)[0];
                const number = parseInt(lastNo.match(/\d+/)[0]) + 1;
                return `${prefix}${number.toString().padStart(4, '0')}`;
            }
            return "PUR-0001";
        } catch (err) {
            console.error("Error fetching purchase number", err);
            return "PUR-" + Math.floor(1000 + Math.random() * 9000); // Fallback
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            const [vRes, tRes, fRes, sRes] = await Promise.all([
                axios.get("/master/vendors"),
                axios.get("/master/raw_material_stocks"),
                axios.get("/master/fabrics"),
                axios.get("/master/size_variants") 
            ]);
            setVendors(vRes.data);
            setTrims(tRes.data);
            setFabrics(fRes.data);
            setSizeVariants(sRes.data);

            // Auto-generate Purchase No ONLY if not in Edit Mode
            if (!editData) {
                const newNo = await fetchLatestPurchaseNo();
                setFormData(prev => ({ ...prev, purchase_no: newNo }));
            }
        };
        fetchData();
    }, [editData]);

    useEffect(() => {
        if (editData && vendors.length > 0 && trims.length > 0 && fabrics.length > 0) {
            const isFabric = !!editData.fabric_id;
            let updatedData = { 
                ...editData,
                item_name: isFabric ? editData.fabric_content : editData.raw_material_name,
                date: editData.date || new Date().toISOString().split("T")[0],
                invoice_date: editData.invoice_date || ""
            };

            const selectedVendor = vendors.find(v => v.vendor_id === parseInt(editData.vendor_id));
            if (selectedVendor) {
                updatedData.phone = selectedVendor.phone_no;
                updatedData.address = selectedVendor.address;
                updatedData.vendor_name = selectedVendor.vendor_name;
            }

            setFormData(updatedData);
            setItemType(isFabric ? "fabric" : "trim");
            setIsNewStock(false);
            setItemList([{...updatedData, item_category: isFabric ? "fabric" : "trim"}]);
        }
    }, [editData, vendors, trims, fabrics]);

    useEffect(() => {
        const qty = parseFloat(formData.quantity || 0);
        const rate = parseFloat(formData.rate || 0);
        setFormData(prev => ({ ...prev, amount: (qty * rate).toFixed(2) }));
    }, [formData.quantity, formData.rate]);

    const handleChange = (e) => {
        const { name, value, checked } = e.target;
        
        if (name === "is_new_stock") {
            setIsNewStock(checked);
            setFormData(prev => ({
                ...prev,
                raw_material_id: "",
                fabric_id: "",
                size_id: "", 
                item_name: "",
                unit: checked ? (itemType === "fabric" ? "Meter" : "") : prev.unit,
                size: "",
                finish: "",
                length: "",
                width: "",
                gsm: "",
                shade_no: "",
                roll_no: "",
                colour: ""
            }));
            return;
        }

        setFormData(prev => {
            let updated = { ...prev, [name]: value };

            if (name === "vendor_id") {
                const selected = vendors.find(v => v.vendor_id === parseInt(value));
                if (selected) {
                    updated.phone = selected.phone_no;
                    updated.address = selected.address;
                    updated.vendor_name = selected.vendor_name;
                }
            }

            if (name === "size" && isNewStock) {
                const selectedSize = sizeVariants.find(s => s.size === value);
                if (selectedSize) {
                    updated.size_id = selectedSize.id || selectedSize.size_id;
                    updated.size_type = selectedSize.type;
                }
            }

            if (!isNewStock) {
                if (name === "raw_material_id") {
                    const selected = trims.find(t => t.raw_material_id === parseInt(value));
                    if (selected) {
                        updated.item_name = selected.raw_material_name;
                        updated.unit = selected.unit;
                        updated.size = selected.size;
                        updated.size_type = selected.size_type;
                        updated.size_id = selected.size_id;
                    }
                }

                if (name === "fabric_id") {
                    const selected = fabrics.find(f => f.fabric_id === parseInt(value));
                    if (selected) {
                        updated.item_name = selected.fabric_content;
                        updated.finish = selected.finish || "";
                        updated.length = selected.length || "";
                        updated.width = selected.width || "";
                        updated.gsm = selected.gsm || "";
                        updated.shade_no = selected.shade_no || "";
                        updated.roll_no = selected.roll_no || "";
                        updated.colour = selected.colour || "";
                        updated.unit = "Meter";
                    }
                }
            }
            return updated;
        });
    };

    const addItemToList = () => {
        if (!formData.item_name || !formData.quantity || !formData.rate) {
            Swal.fire("Error", "Please fill item name, quantity and rate", "error");
            return false;
        }

        let finalSizeId = formData.size_id;
        if (itemType === "trim" && formData.size && !finalSizeId) {
            const found = sizeVariants.find(s => s.size === formData.size);
            if (found) finalSizeId = found.id || found.size_id;
        }
        
        const newItem = {
            ...formData,
            size_id: finalSizeId, 
            item_category: itemType,
            is_new_stock: isNewStock
        };

        if (editingIndex !== null) {
            const updatedList = [...itemList];
            updatedList[editingIndex] = newItem;
            setItemList(updatedList);
            setEditingIndex(null);
        } else {
            setItemList([...itemList, newItem]);
        }

        setFormData(prev => ({
            ...prev,
            raw_material_id: "",
            fabric_id: "",
            size_id: "", 
            quantity: "",
            rate: "",
            amount: "0.00",
            item_name: "",
            finish: "",
            length: "",
            width: "",
            gsm: "",
            shade_no: "",
            roll_no: "",
            colour: "",
            size: "",
            size_type: ""
        }));
        setIsNewStock(false); 
        return true;
    };

    const handleEditItem = (index) => {
        const itemToEdit = itemList[index];
        setEditingIndex(index);
        setItemType(itemToEdit.item_category);
        setIsNewStock(itemToEdit.is_new_stock);
        setFormData(prev => ({
            ...prev,
            ...itemToEdit
        }));
    };

    const removeItem = (index) => {
        setItemList(itemList.filter((_, i) => i !== index));
        if (editingIndex === index) setEditingIndex(null);
    };

    const handleSubmit = async () => {
        if (!formData.vendor_id) {
            Swal.fire("Error", "Please select a Vendor", "error");
            return;
        }

        if (!formData.purchase_no) {
            Swal.fire("Error", "Purchase Number is required", "error");
            return;
        }

        let currentItems = [...itemList];

        const finalItems = currentItems.map(item => {
            let sid = item.size_id;
            if (item.item_category === "trim" && !sid && item.size) {
                const found = sizeVariants.find(s => s.size === item.size);
                sid = found ? (found.id || found.size_id) : null;
            }
            return { ...item, size_id: sid };
        });

        if (finalItems.length === 0) {
            Swal.fire("Error", "Add at least one item to the bill", "error");
            return;
        }

        try {
            if (editData) {
                const updatePayload = {
                    purchase_no: formData.purchase_no,
                    date: formData.date,
                    invoice_no: formData.invoice_no,
                    invoice_date: formData.invoice_date,
                    vendor_id: formData.vendor_id,
                    ...finalItems[0]
                };
                await axios.put(`/purchase_bill/purchases/${editData.purchase_bill_id}`, updatePayload);
            } else {
                const finalPayload = {
                    purchase_no: formData.purchase_no,
                    date: formData.date,
                    invoice_no: formData.invoice_no,
                    invoice_date: formData.invoice_date,
                    vendor_id: formData.vendor_id,
                    items: finalItems
                };
                await axios.post("/purchase_bill/purchases", finalPayload);
            }
            Swal.fire({ icon: "success", text: "Entry Saved", timer: 1500, showConfirmButton: false });
            if (onClose) onClose();
            if (onSuccess) onSuccess();
        } catch (err) {
            Swal.fire({ icon: "error", text: err.response?.data?.error || "Error saving data" });
        }
    };

    const unsavedAmount = (formData.item_name && formData.quantity && formData.rate && itemList.length === 0) 
        ? parseFloat(formData.amount) 
        : 0;

    const totalBillAmount = (itemList.reduce((sum, item) => sum + parseFloat(item.amount), 0) + (itemList.length === 0 ? unsavedAmount : 0)).toFixed(2);

    return (
        <div className="w-full bg-[#f8fafc] rounded-xl shadow-md border border-gray-200 overflow-hidden">
            {/* Header Section */}
            <div className="flex justify-between items-center p-4 bg-white border-b">
                <h2 className="text-lg font-bold text-[var(--sidebar-color)]">
                    {editData ? "Update" : "Add"} Purchase Bill
                </h2>
                <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                    <FaTimesCircle size={22} />
                </button>
            </div>

            <div className="p-5">
                {/* Main Form Fields */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div>
                        <label className="block text-xs font-bold text-gray-500 mb-1 uppercase tracking-tight">Issue Date</label>
                        <input name="date" type="date" value={formData.date} onChange={handleChange} className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-1 focus:ring-[var(--sidebar-color)] outline-none bg-white" />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-500 mb-1 uppercase tracking-tight">Purchase No (Auto)</label>
                        <input name="purchase_no" value={formData.purchase_no} onChange={handleChange} placeholder="Fetching No..." className="w-full border border-gray-300 rounded-lg p-2.5 text-sm font-bold text-blue-700 focus:ring-1 focus:ring-[var(--sidebar-color)] outline-none bg-blue-50" />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-500 mb-1 uppercase tracking-tight">Invoice No</label>
                        <input name="invoice_no" value={formData.invoice_no} onChange={handleChange} placeholder="Select Order" className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-1 focus:ring-[var(--sidebar-color)] outline-none bg-white" />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-500 mb-1 uppercase tracking-tight">Invoice Date</label>
                        <input name="invoice_date" type="date" value={formData.invoice_date} onChange={handleChange} className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-1 focus:ring-[var(--sidebar-color)] outline-none bg-white" />
                    </div>
                </div>

                {/* Vendor Section */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 p-4 bg-white border border-gray-100 rounded-xl shadow-sm">
                    <div>
                        <label className="block text-xs font-bold text-gray-600 mb-1 uppercase tracking-wide">Vendor *</label>
                        <select required name="vendor_id" value={formData.vendor_id} onChange={handleChange} className="w-full border border-gray-300 rounded-lg p-2.5 text-sm bg-gray-50 outline-none">
                            <option value="">Select Vendor</option>
                            {vendors.map(v => <option key={v.vendor_id} value={v.vendor_id}>{v.vendor_name}</option>)}
                        </select>
                    </div>
                    <div><label className="block text-xs font-bold text-gray-400 mb-1 uppercase">Phone</label><input name="phone" value={formData.phone} readOnly className="w-full border border-gray-200 rounded-lg p-2.5 text-sm bg-gray-100 text-gray-500" /></div>
                    <div><label className="block text-xs font-bold text-gray-400 mb-1 uppercase">Address</label><input name="address" value={formData.address} readOnly className="w-full border border-gray-200 rounded-lg p-2.5 text-sm bg-gray-100 text-gray-500" /></div>
                </div>

                {/* Item Entry Section */}
                <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden mb-6">
                    <div className="flex justify-between items-center px-5 py-3 border-b bg-gray-50">
                        <div className="flex gap-3">
                            <button type="button" onClick={() => setItemType("trim")} className={`px-4 py-1.5 text-xs font-bold rounded-md transition-all ${itemType === "trim" ? "bg-[var(--sidebar-color)] text-white" : "bg-white text-gray-500 border border-gray-300"}`}>TRIMS</button>
                            <button type="button" onClick={() => setItemType("fabric")} className={`px-4 py-1.5 text-xs font-bold rounded-md transition-all ${itemType === "fabric" ? "bg-[var(--sidebar-color)] text-white" : "bg-white text-gray-500 border border-gray-300"}`}>FABRIC</button>
                        </div>
                        <label className="flex items-center gap-2 font-bold text-gray-500 cursor-pointer text-[10px] uppercase">
                            <input type="checkbox" name="is_new_stock" checked={isNewStock} onChange={handleChange} className="w-3.5 h-3.5 rounded" />
                            Manual Entry (New Item)
                        </label>
                    </div>

                    <div className="p-5">
                        {itemType === "trim" ? (
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                <div className="col-span-2">
                                    <label className="block text-xs font-bold text-gray-400 mb-1 uppercase">Trim Name</label>
                                    {isNewStock ? (
                                        <input name="item_name" value={formData.item_name} onChange={handleChange} placeholder="Type Item Name" className="w-full border border-gray-300 rounded-lg p-2.5 text-sm" />
                                    ) : (
                                        <select name="raw_material_id" value={formData.raw_material_id} onChange={handleChange} className="w-full border border-gray-300 rounded-lg p-2.5 text-sm bg-white">
                                            <option value="">Select Trim</option>
                                            {trims.map(t => <option key={t.raw_material_id} value={t.raw_material_id}>{t.raw_material_name} ({t.size})</option>)}
                                        </select>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-400 mb-1 uppercase">Size</label>
                                    {isNewStock ? (
                                        <select name="size" value={formData.size} onChange={handleChange} className="w-full border border-gray-300 rounded-lg p-2.5 text-sm">
                                            <option value="">Select Size</option>
                                            {sizeVariants.map(s => <option key={s.id || s.size_id} value={s.size}>{s.size}</option>)}
                                        </select>
                                    ) : (
                                        <input name="size" value={formData.size} disabled className="w-full border border-gray-200 rounded-lg p-2.5 text-sm bg-gray-50" />
                                    )}
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-400 mb-1 uppercase">Unit</label>
                                    {isNewStock ? (
                                        <select name="unit" value={formData.unit} onChange={handleChange} className="w-full border border-gray-300 rounded-lg p-2.5 text-sm">
                                            <option value="">Select Unit</option>
                                            <option value="kg">kg</option>
                                            <option value="Meter">Meter</option>
                                            <option value="piece">piece</option>
                                            <option value="roll">roll</option>
                                        </select>
                                    ) : (
                                        <input name="unit" value={formData.unit} disabled className="w-full border border-gray-200 rounded-lg p-2.5 text-sm bg-gray-50" />
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                                <div className="md:col-span-2">
                                    <label className="block text-xs font-bold text-gray-400 mb-1 uppercase">Fabric Content</label>
                                    {isNewStock ? (
                                        <input name="item_name" value={formData.item_name} onChange={handleChange} placeholder="Type Fabric Name" className="w-full border border-gray-300 rounded-lg p-2.5 text-sm" />
                                    ) : (
                                        <select name="fabric_id" value={formData.fabric_id} onChange={handleChange} className="w-full border border-gray-300 rounded-lg p-2.5 text-sm bg-white">
                                            <option value="">Select Fabric</option>
                                            {fabrics.map(f => <option key={f.fabric_id} value={f.fabric_id}>{f.fabric_content} ({f.roll_no})</option>)}
                                        </select>
                                    )}
                                </div>
                                <div><label className="block text-xs font-bold text-gray-400 mb-1 uppercase">Finish</label><input name="finish" value={formData.finish} onChange={handleChange} disabled={!isNewStock} className="w-full border border-gray-300 rounded-lg p-2.5 text-sm" /></div>
                                <div><label className="block text-xs font-bold text-gray-400 mb-1 uppercase">Length</label><input name="length" value={formData.length} onChange={handleChange} disabled={!isNewStock} className="w-full border border-gray-300 rounded-lg p-2.5 text-sm" /></div>
                                <div><label className="block text-xs font-bold text-gray-400 mb-1 uppercase">Width</label><input name="width" value={formData.width} onChange={handleChange} disabled={!isNewStock} className="w-full border border-gray-300 rounded-lg p-2.5 text-sm" /></div>
                                <div><label className="block text-xs font-bold text-gray-400 mb-1 uppercase">GSM</label><input name="gsm" value={formData.gsm} onChange={handleChange} disabled={!isNewStock} className="w-full border border-gray-300 rounded-lg p-2.5 text-sm" /></div>
                                <div><label className="block text-xs font-bold text-gray-400 mb-1 uppercase">Shade No</label><input name="shade_no" value={formData.shade_no} onChange={handleChange} disabled={!isNewStock} className="w-full border border-gray-300 rounded-lg p-2.5 text-sm" /></div>
                                <div><label className="block text-xs font-bold text-gray-400 mb-1 uppercase">Roll No</label><input name="roll_no" value={formData.roll_no} onChange={handleChange} disabled={!isNewStock} className="w-full border border-gray-300 rounded-lg p-2.5 text-sm" /></div>
                                <div><label className="block text-xs font-bold text-gray-400 mb-1 uppercase">Colour</label><input name="colour" value={formData.colour} onChange={handleChange} disabled={!isNewStock} className="w-full border border-gray-300 rounded-lg p-2.5 text-sm" /></div>
                                <div><label className="block text-xs font-bold text-gray-400 mb-1 uppercase">Unit</label><input name="unit" value={formData.unit} disabled className="w-full border border-gray-200 rounded-lg p-2.5 text-sm bg-gray-50" /></div>
                            </div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-5 pt-5 border-t border-gray-100 items-end">
                            <div><label className="block text-[10px] font-bold text-gray-700 mb-1 uppercase">Quantity</label><input name="quantity" value={formData.quantity} onChange={handleChange} placeholder="Qty" className="w-full border border-gray-300 rounded-lg p-2.5 text-sm font-semibold" /></div>
                            <div><label className="block text-[10px] font-bold text-gray-700 mb-1 uppercase">Rate</label><input name="rate" value={formData.rate} onChange={handleChange} placeholder="Rate" className="w-full border border-gray-300 rounded-lg p-2.5 text-sm font-semibold" /></div>
                            <div><label className="block text-[10px] font-bold text-gray-700 mb-1 uppercase">Amount</label><input value={formData.amount} readOnly className="w-full border border-gray-200 rounded-lg p-2.5 text-sm bg-gray-50 font-bold" /></div>
                            
                            <button type="button" onClick={addItemToList} className={`h-[44px] rounded-lg flex items-center justify-center gap-2 font-bold text-xs transition-all shadow-sm ${editingIndex !== null ? 'bg-amber-400 text-white' : 'bg-[var(--icon-color)] text-[var(--sidebar-color)] hover:brightness-95'}`}>
                                {editingIndex !== null ? <FaEdit /> : <FaPlus />} {editingIndex !== null ? "UPDATE ITEM" : "ADD TO LIST"}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Table Section */}
                <div className="mb-6 rounded-lg border border-gray-200 overflow-hidden shadow-sm bg-white">
                    <table className="w-full border-collapse">
                        <thead className="bg-[#2d3748] text-white text-[10px] uppercase">
                            <tr>
                                <th className="px-4 py-2.5 text-left">Item Type</th>
                                <th className="px-4 py-2.5 text-left">Select Item</th>
                                <th className="px-4 py-2.5 text-center">Size</th>
                                <th className="px-4 py-2.5 text-center">Quantity</th>
                                <th className="px-4 py-2.5 text-center">Rate</th>
                                <th className="px-4 py-2.5 text-center">Amount</th>
                                <th className="px-4 py-2.5 text-center">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {[...itemList].reverse().map((item, idx) => (
                                <tr key={idx} className="text-xs">
                                    <td className="px-4 py-3"><span className="bg-gray-100 px-2 py-0.5 rounded border text-gray-600 capitalize">{item.item_category}</span></td>
                                    <td className="px-4 py-3 font-medium">{item.item_name} {item.roll_no ? `[R: ${item.roll_no}]` : ''}</td>
                                    <td className="px-4 py-3 text-center text-gray-500">{item.size || "-"}</td>
                                    <td className="px-4 py-3 text-center font-bold text-gray-700">{item.quantity} {item.unit}</td>
                                    <td className="px-4 py-3 text-center font-bold text-gray-700">{parseFloat(item.rate || 0).toFixed(2)}</td>
                                    <td className="px-4 py-3 text-center font-bold text-gray-700">{parseFloat(item.amount || 0).toFixed(2)}</td>
                                    <td className="px-4 py-3">
                                        <div className="flex gap-3 justify-center">
                                            <button type="button" onClick={() => handleEditItem(itemList.length - 1 - idx)} className="text-blue-500 hover:scale-110"><FaEdit size={16} /></button>
                                            <button type="button" onClick={() => removeItem(itemList.length - 1 - idx)} className="text-red-400 hover:text-red-600"><FaTrash size={16} /></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Footer Section */}
                <div className="flex flex-col md:flex-row justify-between items-center gap-4 bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
                    <div className="flex items-baseline gap-3">
                        <span className="text-gray-400 font-bold text-[10px] uppercase">Grand Total</span>
                        <span className="text-3xl font-black text-[var(--sidebar-color)]">₹ {totalBillAmount}</span>
                    </div>

                    <div className="flex gap-3 w-full md:w-auto">
                        <button type="button" onClick={onClose} className="flex-1 px-8 py-3 bg-white text-gray-500 rounded-lg font-bold text-sm border border-gray-300 hover:bg-gray-50 transition-all">
                            CANCEL
                        </button>
                        <button type="button" onClick={handleSubmit} className="flex-[2] px-12 py-3 bg-[var(--sidebar-color)] text-white rounded-lg font-bold text-sm shadow-md hover:brightness-110 transition-all uppercase tracking-wide">
                            {editData ? "Update Bill" : "Save Entries"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}